import { ZodSchema, z } from "zod";
import {
  AccountPlan as PrismaAccountPlan,
  PaidFeature as PrismaPaidFeature,
} from "@prisma/client";
import { AccountEntitlementsResponse } from "../services/zapier/types/types";
import { components } from "@zapier/api-clients/apis/permissions/types";
import { operations } from "@zapier/api-clients/apis/interfaces/types";
import { ZapierSession } from "../sessions/session";

export type InterfaceOperations = operations;

export type GetJsonResponseFromOperation<
  OperationId extends keyof InterfaceOperations,
  StatusCode extends keyof InterfaceOperations[OperationId]["responses"],
> = InterfaceOperations[OperationId] extends {
  responses: Record<StatusCode, any>;
}
  ? InterfaceOperations[OperationId]["responses"][StatusCode]["content"]["application/json"]
  : never;

export type Operation = {
  type: string;
  path: string;
  validator: ZodSchema;
};

// When adding a new feature, add it to the PaidFeature enum in schema.prisma
export const paidFeatures = z.nativeEnum(PrismaPaidFeature);
export type PaidFeature = z.infer<typeof paidFeatures>;

export const accountPlan = z.nativeEnum(PrismaAccountPlan);
export type AccountPlan = z.infer<typeof accountPlan>;

// You can find the list of available Permission levels under 'type Interface' here:
// https://gitlab.com/zapier/team-identity-platform/fga-authorization-models/-/blob/main/models/model.fga?ref_type=heads
export const permissionLevel = z.enum([
  "can_edit",
  "can_view",
  "can_view_metadata",
  "can_view_via_shares",
  "can_delete",
  "can_change_owner",
  "can_share_editor",
  "can_view_shares",
  "can_manage_editors",
  "can_view_interface",
  "can_delete_interface",
  "can_edit_interface",
  "can_add_page",
  "can_delete_page",
  "can_edit_page",
  "can_view_page",
  "can_view_consumer",
  "can_add_consumer",
  "can_delete_consumer",
  "can_edit_consumer",
  "can_edit_theme",
  "can_view_theme",
  "can_add_custom_domain",
  "can_delete_custom_domain",
  "can_view_custom_domain",
  "can_add_block",
  "can_edit_block",
  "can_view_block",
  "can_view_button",
  "can_add_subscription",
  "can_delete_subscription",
  "can_add_zap",
  "can_view_zaps",
  "can_manage_zaps",
  "can_delete_zap",
  "can_add_datasource",
  "can_manage_datasource",
  "can_view_payments",
  "can_view_cli_auth",
  "can_check_cli_auth",
  "can_edit_auth",
  "can_check_auth",
  "can_edit_captcha",
  "can_edit_idp_settings",
  "can_view_idp_settings",
  "can_delete_form",
  "can_view_form",
  "can_view_form_output_fields",
  "can_view_form_submissions",
  "can_access_all_interfaces",
  "can_change_account",
] as const);
export type PermissionLevel = z.infer<typeof permissionLevel>;

export const premiumFeatures: PaidFeature[] = [
  "customColors",
  "branding",
  "embedding",
  "analytics",
  "customTokenLength",

  "passwordProtect",
  "managedUsers",

  "chatBotDataSources",
  "conditionalLogic",
  "fileSizeLimit10mb",
  "embedBlock",
  "layoutBlock",
  "stripePayment",
  "extendedFileUploads",

  /**
   * Stytch-related project authorization
   */
  "stytchAuth",
  "projectMagicLinkAuth",
];

export const advancedFeatures: PaidFeature[] = [
  ...premiumFeatures,
  "customDomain",
  "dynamicFilters",
  "hideZapierButton",
  "fileSizeLimit25mb",

  /**
   * Stytch-related project authorization
   */
  "projectSignInWithGoogleAuth",
  "projectApprovedDomainsAuth",
];

export const teamAccountFeatures: PaidFeature[] = ["collaborators"];

export const allFeatures: PaidFeature[] = Array.from(
  new Set(premiumFeatures.concat(advancedFeatures, teamAccountFeatures))
);

export const LOGIN_PAGE_OPERATIONS = [
  /**
   * For checking what kind of authorization methods are set for the project in Stytch.
   */
  "v0_get_stytch_organization",
  "v0_stytch_auth_start",
  "v0_google_oauth_redirect",
  "v0_managed_consumer_auth_start",
  "v0_check_password",
  "v0_managed_consumer_auth_check",
] as const satisfies (keyof InterfaceOperations)[];

export type LoginPageOperations = (typeof LOGIN_PAGE_OPERATIONS)[number];

export const PUBLISHED_PAGE_OPERATIONS = [
  "v0_init_chatbot",
  "v0_get_chatbot_session_suggestions",
  "v0_create_chatbot_event",
  "v0_list_pages",
  "v0_get_field_block_uploads_signature",
  "v0_get_field_block_uploads_status",
  "v0_button_trigger_click",
  "v0_get_consumer_me",
  "v0_create_form_submission",
  "v0_create_stripe_checkout_session",
  "v0_get_stripe_checkout_session_status",
  "v0_trigger_stripe_payment_processed",
  "v0_logout",
  "v0_verify_browser",
  "v0_get_checklist_fields",
  "v0_update_checklist_record",
  "v0_get_checklist_list_item_details",
  "v0_get_checklist_records",
  "v0_create_checklist_record",
  "v0_delete_checklist_record",
  "v0_get_page",
  "v0_get_table_record",
  "v0_list_table_fields",
  "v0_get_table",
  "v0_query_table_records",
  "v0_aigenerated_query_table_records",
  "v0_list_table_block_fields",
  /**
   * The login page lives on the published page.
   */
  ...LOGIN_PAGE_OPERATIONS,
] as const satisfies (keyof InterfaceOperations)[];

export type PublishedPageOperations =
  (typeof PUBLISHED_PAGE_OPERATIONS)[number];

export const OPERATIONS_EXCLUDED_FROM_AUTHORIZATION = [
  "chatbotsInterfaceCreate",
  "v0_list_interfaces",
  "projectsCreate",
  "getProjectMetadata",
  /**
   * These operations must be able to throw specific errors when session is not defined.
   * To ensure we can do this, we exclude them from authorization flow, and handle the errors in the handler.
   */
  "v0_sync_current_user",
  "v0_refresh_entitlements",
  /**
   * This endpoint works purely with the session data.
   * We should not need to check for authorization here.
   */
  "v0_current_user",
  // This endpoint is for internal anonymous service JWT operations
  // We verify the JWT is minted by chatbotsapi and has an interfaces audience in the handler
  "v0_transfer_chatbots_interface",
  /**
   * These endpoints are used by zsl-api for service-only jwt access to read-only
   * interface configuration data.
   */
  "v0_internal_get_interface",
  "v0_internal_list_pages",

  /**
   * For the health check endpoints, we look for the API key in the header.
   * We perform that check in the handler itself.
   */
  "v0_get_health",
  "v0_get_health_plus",

  /**
   * In TRPC-land, we did not have any authentication or authorization checks for this endpoint.
   * See https://gitlab.com/zapier/team-fpv/interfaces/-/blob/bdc413b1d8c075c0470ca9313e425313003262e0/packages/service/src/server/routers/split.ts
   *
   * When we first implemented this procedure as an OpenAPI endpoint, we added more strict checks.
   * Sadly, this caused a regression where users were getting 403/401 errors when accessing the endpoint.
   *
   * We did not manage to uncover the root cause of this regression.
   * See https://zapier.slack.com/archives/C048W40M39S/p1749501966030349
   * See https://zapier.slack.com/archives/C048W40M39S/p1749072267923979
   */
  "v0_get_feature_flags",
] as const satisfies (keyof InterfaceOperations)[];

export const HEALTH_CHECK_API_KEY_HEADER_NAME = "x-health-check-api-key";

type OperationsExcludedFromAuthorization =
  (typeof OPERATIONS_EXCLUDED_FROM_AUTHORIZATION)[number];

export type RequestContextUser = {
  id: string;
  zapierId: number;
  accountIds: number[];
  currentAccountId: number;
  isStaff: boolean;
  entitlements: AccountEntitlementsResponse;
  email: string;
};

export type RequestContext = {
  user?: RequestContextUser | null;
  jwt?: string | null;
};

export type AssetType = components["schemas"]["AssetType689Enum"];

/**
 * A null permissionLevel means that a Permission API check is not required for the operation. This could be due to one of the following reasons:
 * 1. The operation is only authorized by authenticating the user.
 * 2. The operation is authorized by being a member of the account. Account level checks are not handled by Permissions API yet.
 * 3. The operation is for a pass through endpoint. We simply proxy the request to tables, monolith or other services.
 */
export const PERMISSION_CHECK_NOT_REQUIRED = null;

export type TrpcPermissionConfig = {
  /**
   * The permission level required to access the operation.
   *
   * This is used to determine if the user has the necessary permissions to access the operation.
   */
  permissionLevel: PermissionLevel | typeof PERMISSION_CHECK_NOT_REQUIRED;

  /**
   * Whether we use the new Permissions API and feature access check logic to check for access or the old logic.
   *
   * This flag will be removed when we finish roll out of the new Permissions API and feature access check logic
   */
  enabled: boolean;
};

export type PermissionConfig = {
  zapierId: number;
  currentAccountId: number;
  assetType: AssetType;
  permissionLevel: PermissionLevel;
  assetId: string;
};

export type OperationsIdsThatNeedAuthorization = Exclude<
  keyof InterfaceOperations,
  OperationsExcludedFromAuthorization
>;

export type InterfaceOperationAuthorizationMap = {
  [Id in OperationsIdsThatNeedAuthorization]: {
    operationId: Id;
    parameters: InterfaceOperations[Id] extends { parameters: infer Parameters }
      ? Parameters
      : undefined;
    context: RequestContext;
    session?: ZapierSession;
  } & (InterfaceOperations[Id] extends {
    requestBody?: {
      content: {
        "application/json": infer RequestBody;
      };
    };
  }
    ? { body: RequestBody }
    : { body?: never });
};

export type InterfaceOperationForAuthorization = {
  [Id in OperationsIdsThatNeedAuthorization]: InterfaceOperationAuthorizationMap[Id];
}[OperationsIdsThatNeedAuthorization];

export type BuilderOperationAuthorizationRequest =
  InterfaceOperationAuthorizationMap[OperationsIdsThatNeedAuthorization];

export type BuilderOperationPermissionsMap = {
  [Id in OperationsIdsThatNeedAuthorization]: OperationPermissionConfig<Id>;
};

/**
 * All specified configs in OperationPermissionConfig must pass for the operation to be authorized.
 *
 * If you want to authorize the operation based on a single config, specify the config alone.
 * Specifying multiple configs, means all specified configs must pass.
 *
 */
export type OperationPermissionConfig<
  T extends
    OperationsIdsThatNeedAuthorization = OperationsIdsThatNeedAuthorization,
> = {
  /**
   * The permission level required to access the operation.
   *
   * This is used to determine if the user has the necessary permissions to access the operation.
   */
  permissionLevel: PermissionLevel | null;
  /**
   * The feature access required to access the operation.
   */
  featureAccess?: {
    /**
     * Access is granted if the interface has at least one of the features.
     */
    anyOf?: PaidFeature[];
    /**
     * Access is granted if the interface has ALL of these features.
     */
    allOf?: PaidFeature[];
    /**
     * Access is only granted if the account is on the paid plan or the user is the creator of the project. This is similar to 'collaborators' feature access.
     *
     * We can get rid of this check, if we confirm that when a user downgrades a plan, we remove the other editors they might have added to the project.
     * That way we trust Permissions API 100% and we also save extra calls to the DB. But having it here helps us maintain parity with legacy authz.
     */
    requiresPaidPlanOrIsProjectCreator?: boolean;
  };
  /**
   * Custom logic to check for access
   */
  customPermissionCheck?: (
    request: InterfaceOperationAuthorizationMap[T]
  ) => Promise<boolean>;
};

export type PublishedOperationAuthorizationRequest = {
  [Id in PublishedPageOperations | LoginPageOperations]: Omit<
    InterfaceOperationAuthorizationMap[Id],
    "context"
  >;
}[PublishedPageOperations | LoginPageOperations];

export type LoginPageOperationAuthorizationRequest = {
  [Id in LoginPageOperations]: Omit<
    InterfaceOperationAuthorizationMap[Id],
    "context"
  >;
}[LoginPageOperations];
